// reservePresets.ts — the self-documenting spec behind the ONE reserve card.
//
// House style (memory settings_ui_self_documenting_presets): a risk-deciding admin setting is three
// presets + Custom, each carrying plain consequence text. Contract §11 Stage 3.5 fixes the three:
// Tight a_z 0.30 / Balanced 0.15 (the default) / Wild 0.08, and fixes what each line must state —
// B in rupees, the error over ₹1 lakh, and THE MEASURED LADDER.
//
// ════ WHERE EVERY NUMBER IN THIS FILE COMES FROM — nothing here is a guess ════════════════════
//
//  SIZING (sigmaRef, sBar, boundPaise15, maxSinglePayoutRupees, confidence)
//    shared/src/db/migrations/168_game_reserve_sizing.sql, and the measurement it was committed
//    with: scratch/reserve-sizing-2026-09-14.{txt,json} (scripts/reserve-sizing-sweep.mjs, seed
//    20260914, ≤200,000 rounds/cell, a_z 0.15). Every σ cell drove that game's REAL exported
//    decision function. `confidence` is the migration's own honest column: σ is measured from the
//    engines, s̄ is CONFIG on six of eight games because the platform has ~498 lifetime bets.
//
//  LADDER (k0Pct, k6Pct, riseLcbPt, samples) and takePct
//    scratch/admin-card/ladder-by-preset.mjs, run 2026-09-15, seed 20260906, 1,000,000 rounds per
//    cell, on each game's σ_ref board at its σ_ref stake with the bounded reserve INJECTED in the
//    harness at that preset's sizing — never in the database. Wilson intervals with a Bonferroni z
//    over the admitted rungs at α=0.05, MIN_SAMPLE 80, all copied verbatim from
//    scripts/martingale-tell-gate.mjs. `riseLcbPt` is lo(k6) − hi(k0): the 95% LOWER bound on the
//    rise. **It is what decides whether this file is allowed to say a player's chance "rises".**
//    ≤ 0 means the run cannot tell the two rungs apart from chance and the copy says so.
//
//  CEILING (takeCeilingPct, takeCeilingBoard)
//    scratch/admin-card/take-ceiling.mjs (each game's σ_ref board) and take-ceiling-flat.mjs (the
//    FLATTEST board the four games with a board menu offer) — amendment A6, measured, not reasoned
//    about: the board driven at ascending dials until the realised take stops following the dial.
//    The LOWER of the two is what the card carries, because a player picks the board.
//
//  CLAMP (azNoBindMax)
//    scratch/reserve-sizing-2026-09-14.json, reach/σ per board: the largest a_z at which
//    `expTilt.js`'s symmetric clamp does not clip the aim on this game's worst board. Above it the
//    extra authority is partly ignored — the card must say so rather than pretend it lands.
//
// Re-measure and regenerate whenever a multiplier table, max_bet, a payout cap or target_profit_pct
// changes on a game — §3.2 requires exactly that of B and R_lim, and the ladder moves with them.

export type ReservePresetKey = 'tight' | 'balanced' | 'wild';

export type LadderCell = {
  /** realised take over the run, % of turnover (the dial it was driven at is `targetPct`) */
  takePct: number;
  /** P(this round pays more than it cost) with no losing run behind it */
  k0Pct: number;
  /** …and after six losses in a row */
  k6Pct: number;
  n0: number;
  n6: number;
  /** 95% joint lower bound on (k6 − k0). > 0 ⇒ the rise is real; ≤ 0 ⇒ not distinguishable. */
  riseLcbPt: number;
};

export type ReserveGameSpec = {
  game: string;
  /** the board the row was sized from and the ladder measured on */
  board: string;
  /** the stake that board was driven at (s̄ from the sizing sweep) */
  stakeRupees: number;
  /** the dial the ladder was measured at — this game's configured target */
  targetPct: number;
  sigmaRef: number;
  sBar: number;
  confidence: 'CONFIG' | 'CONFIG*' | 'THIN';
  /** B in paise at a_z = 0.15 as migration 168 actually STORES it — carried for cross-checking, not
   *  for arithmetic: `sizingAt` re-derives B from σ_ref and s̄ the way the sizing sweep did. */
  boundPaise15: number;
  /** what amendment A4's 1.2x floor is taken against */
  maxSinglePayoutRupees: number;
  /** '3B' or '1.2xPayout' — which half of max(3B, 1.2·payout) is binding at a_z 0.15 */
  limitDriver15: '3B' | '1.2xPayout';
  /** the largest authority this game's worst board takes without the symmetric clamp clipping it */
  azNoBindMax: number;
  /** A6: the highest dial whose realised take is within 0.5pt of it, taken as the LOWEST ceiling
   *  across the boards measured for this game — the player picks the board, so the binding one is
   *  the one the card must warn on. null = not measured. */
  takeCeilingPct: number | null;
  /** …and which board produced that ceiling. */
  takeCeilingBoard: string | null;
  ladder: Record<ReservePresetKey, LadderCell>;
};

export const RESERVE_PRESETS: { key: ReservePresetKey; label: string; az: number; tone: 'safe' | 'recommended' | 'risky' }[] = [
  { key: 'tight', label: 'Tight', az: 0.30, tone: 'risky' },
  { key: 'balanced', label: 'Balanced', az: 0.15, tone: 'recommended' },
  { key: 'wild', label: 'Wild', az: 0.08, tone: 'safe' },
];

// The three constants §2.4 fixes, mirrored from `_shared/reserve.js` (RESERVE_SIZING_K,
// RESERVE_LIMIT_BOUND_MULTIPLE, RESERVE_LIMIT_PAYOUT_MULTIPLE) — the admin bundle cannot import
// server code, so they are named here and `spec-check.mjs` pins the result against migration 168.
export const RESERVE_SIZING_K = 4.5;
export const RESERVE_LIMIT_BOUND_MULTIPLE = 3;
export const RESERVE_LIMIT_PAYOUT_MULTIPLE = 1.2;
export const DEFAULT_AUTHORITY_Z = 0.15;
export const AUTHORITY_MIN = 0.02;
export const AUTHORITY_MAX = 1.0;

/** MEASURED — see the header for the run behind every cell. */
export const RESERVE_GAME_SPECS: Record<string, ReserveGameSpec> = {
  lucky7: {
    game: 'lucky7',
    board: "one player on '7' (X3)",
    stakeRupees: 500,
    targetPct: 10,
    sigmaRef: 1.118033988749895,
    sBar: 500,
    confidence: 'CONFIG',
    boundPaise15: 1677051,
    maxSinglePayoutRupees: 20000,
    limitDriver15: '3B',
    azNoBindMax: 0.447,
    takeCeilingPct: 90,
    takeCeilingBoard: "one player on '7' (X3)",
    ladder: {
      tight: {"takePct":10,"k0Pct":29.42,"k6Pct":31.44,"n0":300001,"n6":35041,"riseLcbPt":1.01},
      balanced: {"takePct":9.999,"k0Pct":29.93,"k6Pct":30.62,"n0":300005,"n6":35200,"riseLcbPt":-0.32},
      wild: {"takePct":9.997,"k0Pct":30.08,"k6Pct":30.34,"n0":300010,"n6":35310,"riseLcbPt":-0.75},
    },
  },
  thimbles: {
    game: 'thimbles',
    board: "single (X2.94)",
    stakeRupees: 73,
    targetPct: 10,
    sigmaRef: 1.352167210216089,
    sBar: 72.58064516129032,
    confidence: 'THIN',
    boundPaise15: 294424,
    maxSinglePayoutRupees: 2940,
    limitDriver15: '3B',
    azNoBindMax: 0.664,
    takeCeilingPct: 90,
    takeCeilingBoard: "single (X2.94)",
    ladder: {
      tight: {"takePct":9.999,"k0Pct":30.02,"k6Pct":31.54,"n0":307012,"n6":33670,"riseLcbPt":0.51},
      balanced: {"takePct":9.999,"k0Pct":30.51,"k6Pct":30.71,"n0":307013,"n6":33959,"riseLcbPt":-0.8},
      wild: {"takePct":9.999,"k0Pct":30.62,"k6Pct":30.59,"n0":307013,"n6":33906,"riseLcbPt":-1.04},
    },
  },
  plinko: {
    game: 'plinko',
    board: "high/16",
    stakeRupees: 100,
    targetPct: 5,
    sigmaRef: 3.412619576182197,
    sBar: 100,
    confidence: 'CONFIG*',
    boundPaise15: 1023786,
    maxSinglePayoutRupees: 50000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.212,
    takeCeilingPct: 50,
    takeCeilingBoard: "low/8",
    ladder: {
      tight: {"takePct":4.998,"k0Pct":19.59,"k6Pct":18.91,"n0":169797,"n6":46643,"riseLcbPt":-1.66},
      balanced: {"takePct":4.994,"k0Pct":19.98,"k6Pct":19.66,"n0":198345,"n6":52463,"riseLcbPt":-1.16},
      wild: {"takePct":4.988,"k0Pct":20.47,"k6Pct":20.31,"n0":203712,"n6":51958,"riseLcbPt":-1.01},
    },
  },
  roulette: {
    game: 'roulette',
    board: "straight-up (29:1)",
    stakeRupees: 100,
    targetPct: 10,
    sigmaRef: 4.864864864864869,
    sBar: 100,
    confidence: 'CONFIG',
    boundPaise15: 1459459,
    maxSinglePayoutRupees: 200000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.167,
    takeCeilingPct: 60,
    takeCeilingBoard: "red (even money)",
    ladder: {
      tight: {"takePct":10,"k0Pct":2.15,"k6Pct":2.4,"n0":30001,"n6":26258,"riseLcbPt":-0.38},
      balanced: {"takePct":10,"k0Pct":2.76,"k6Pct":2.74,"n0":30001,"n6":25354,"riseLcbPt":-0.73},
      wild: {"takePct":9.988,"k0Pct":2.93,"k6Pct":2.84,"n0":30005,"n6":25108,"riseLcbPt":-0.83},
    },
  },
  aviator: {
    game: 'aviator',
    board: "auto cash-out X10.00",
    stakeRupees: 100,
    targetPct: 10,
    sigmaRef: 2.6653617956113753,
    sBar: 100,
    confidence: 'CONFIG',
    boundPaise15: 799609,
    maxSinglePayoutRupees: 200000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.289,
    takeCeilingPct: 90,
    takeCeilingBoard: "auto cash-out X10.00",
    ladder: {
      tight: {"takePct":9.999,"k0Pct":7.93,"k6Pct":8.64,"n0":90001,"n6":53548,"riseLcbPt":0.01},
      balanced: {"takePct":9.997,"k0Pct":8.63,"k6Pct":8.88,"n0":90003,"n6":51800,"riseLcbPt":-0.49},
      wild: {"takePct":9.995,"k0Pct":8.74,"k6Pct":8.9,"n0":90005,"n6":51345,"riseLcbPt":-0.58},
    },
  },
  andar_bahar: {
    game: 'andar_bahar',
    board: "always andar",
    stakeRupees: 100,
    targetPct: 5,
    sigmaRef: 0.9787255896108998,
    sBar: 100,
    confidence: 'CONFIG',
    boundPaise15: 293618,
    maxSinglePayoutRupees: 200000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.97,
    takeCeilingPct: 90,
    takeCeilingBoard: "always andar",
    ladder: {
      tight: {"takePct":5,"k0Pct":48.34,"k6Pct":51.5,"n0":487518,"n6":8118,"riseLcbPt":1.35},
      balanced: {"takePct":4.999,"k0Pct":48.71,"k6Pct":50.06,"n0":487512,"n6":8674,"riseLcbPt":-0.41},
      wild: {"takePct":4.998,"k0Pct":48.81,"k6Pct":49.36,"n0":487519,"n6":8808,"riseLcbPt":-1.19},
    },
  },
  teen_patti: {
    game: 'teen_patti',
    board: "always PLAYER A (X1.98)",
    stakeRupees: 100,
    targetPct: 5,
    sigmaRef: 0.9851257204852555,
    sBar: 100,
    confidence: 'CONFIG',
    boundPaise15: 295538,
    maxSinglePayoutRupees: 200000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.933,
    takeCeilingPct: 90,
    takeCeilingBoard: "always PLAYER A (X1.98)",
    ladder: {
      tight: {"takePct":5.001,"k0Pct":47.48,"k6Pct":50.49,"n0":479224,"n6":8796,"riseLcbPt":1.26},
      balanced: {"takePct":5.001,"k0Pct":47.88,"k6Pct":48.43,"n0":479224,"n6":9356,"riseLcbPt":-1.15},
      wild: {"takePct":5.002,"k0Pct":47.97,"k6Pct":47.59,"n0":479217,"n6":9573,"riseLcbPt":-2.07},
    },
  },
  casino: {
    game: 'casino',
    board: "3x3 / 5 lines",
    stakeRupees: 100,
    targetPct: 10,
    sigmaRef: 2.806372432431448,
    sBar: 100,
    confidence: 'CONFIG',
    boundPaise15: 841912,
    maxSinglePayoutRupees: 250000,
    limitDriver15: '1.2xPayout',
    azNoBindMax: 0.339,
    takeCeilingPct: 90,
    takeCeilingBoard: "3x3 / 5 lines",
    ladder: {
      tight: {"takePct":9.998,"k0Pct":14.72,"k6Pct":15.45,"n0":153204,"n6":57597,"riseLcbPt":-0.05},
      balanced: {"takePct":9.997,"k0Pct":15.33,"k6Pct":15.64,"n0":155541,"n6":56711,"riseLcbPt":-0.47},
      wild: {"takePct":9.994,"k0Pct":15.41,"k6Pct":15.76,"n0":156106,"n6":56578,"riseLcbPt":-0.45},
    },
  },
};

export const RESERVE_GAME_ORDER = [
  'plinko', 'roulette', 'lucky7', 'thimbles', 'casino', 'aviator', 'andar_bahar', 'teen_patti',
];

/* ── the contract's own arithmetic, §2.4 ────────────────────────────────────────────────────────
 *   B(a_z)  = 4.5·σ_ref·s̄ / a_z,  so B(a_z) = B(0.15)·0.15/a_z — the SAME measurement, re-scaled.
 *   R_lim   = max(3B, 1.2 × maxSinglePayout)                                          (A4)
 *   error over any span = 2·R_lim / turnover                       (an identity, not an estimate)
 * A preset that moved a_z WITHOUT re-sizing B would break SD(R) = B/3, which is the whole closure —
 * so the card states the resized B on every chip and the save must write both. */
export function sizingAt(spec: ReserveGameSpec, az: number) {
  // Computed from σ_ref and s̄ — the two MEASURED quantities — and not from the stored B, because
  // the two disagree by up to two paise and the migration is right. `reserve-sizing-sweep.mjs`
  // forms B in RUPEES, takes max(3B, 1.2·payout) in rupees, and rounds to paise once at the end;
  // rounding B to paise first and tripling THAT gives thimbles 883272p where migration 168 holds
  // 883271p. One paisa on ₹8,833 changes nothing about the money and everything about whether this
  // card can be checked against the table it describes — so the card reproduces the sweep exactly,
  // and `spec-check.mjs` asserts equality to the paisa rather than tolerating a gap.
  const bRupees = (RESERVE_SIZING_K * spec.sigmaRef * spec.sBar) / az;
  const boundPaise = Math.round(bRupees * 100);
  const limitPaise = Math.round(Math.max(RESERVE_LIMIT_BOUND_MULTIPLE * bRupees,
    RESERVE_LIMIT_PAYOUT_MULTIPLE * spec.maxSinglePayoutRupees) * 100);
  return {
    boundPaise,
    limitPaise,
    boundRupees: boundPaise / 100,
    limitRupees: limitPaise / 100,
    /** percentage points of take error the identity allows over ₹1 lakh of play */
    errPerLakhPt: (2 * (limitPaise / 100)) / 100000 * 100,
    /** …and over ₹1 crore */
    errPerCrorePt: (2 * (limitPaise / 100)) / 10000000 * 100,
    /** §2.3: detectability = a_z²/4.5 σ of aim shift per round — board-, stake- and σ-independent */
    detectabilitySigmaPerRound: (az * az) / 4.5,
  };
}

const inr = (r: number) => `₹${Math.round(r).toLocaleString('en-IN')}`;
const pt = (n: number) => `${n >= 0 ? '' : '−'}${Math.abs(n).toFixed(Math.abs(n) < 1 ? 2 : 1)}`;

/**
 * The ladder sentence — and the ONE place that decides whether the card may claim a rise.
 * `riseLcbPt > 0` is the 95% joint lower bound on (k6 − k0) being positive; below that the run
 * measured no difference it can tell from chance, and saying "rises from 43% to 45%" would be
 * inventing a number the engine does not produce.
 */
export function ladderSentence(spec: ReserveGameSpec, key: ReservePresetKey): string {
  const c = spec.ladder[key];
  if (!c) return '';
  const after = `After six losses in a row, the next round pays ${c.k6Pct.toFixed(1)}% of the time against ${c.k0Pct.toFixed(1)}% with no losing run behind it`;
  if (c.riseLcbPt > 0) {
    return `${after} — a real rise of at least ${pt(c.riseLcbPt)} points (95% confidence, ${(c.n6 / 1000).toFixed(0)}k occurrences). A player who counts their losses gains something, however small.`;
  }
  return `${after} — a gap this run cannot tell from chance (${(c.n6 / 1000).toFixed(0)}k occurrences, 95% bound ${pt(c.riseLcbPt)} points). Counting losses tells a player nothing.`;
}

/** The full consequence paragraph for one preset chip on one game. */
export function presetConsequence(spec: ReserveGameSpec, key: ReservePresetKey): string {
  const p = RESERVE_PRESETS.find((x) => x.key === key)!;
  const s = sizingAt(spec, p.az);
  const c = spec.ladder[key];
  const parts: string[] = [];
  parts.push(`The game corrects its own drift over about ${inr(s.boundRupees)} of position (that is B, the scale it works on)`
    + `${key === 'tight' ? ' — the shortest leash of the three, so it pulls back hardest' : key === 'wild' ? ' — the longest leash, so it drifts further before it pulls' : ''}.`);
  // §11 Stage 3.5 asks for "the error over ₹1 lakh". At migration 168's sizing that bound is 120
  // points on plinko and 100 on lucky 7 — arithmetically true and completely vacuous, because a take
  // cannot be 120 points off anything. Printing it alone would look like a promise and be none. So
  // the card prints it, says plainly what it is worth, and adds the two figures that DO bite: the
  // turnover at which the same identity closes to one point, and the error at ₹1 crore.
  const tightensAt = 200 * s.limitRupees;         // 2·R_lim/turnover = 1 point  ⇒  turnover = 200·R_lim
  parts.push(s.errPerLakhPt >= 5
    ? `Over ₹1 lakh of play the arithmetic allows ${s.errPerLakhPt.toFixed(1)} points either side of the dial — which is to say it promises nothing over a short run. It closes to one point once about ${inr(tightensAt)} has been played, and to ${s.errPerCrorePt.toFixed(2)} points over ₹1 crore. Those are identities from the limit, not estimates.`
    : `Over ₹1 lakh of play the take cannot sit more than ${s.errPerLakhPt.toFixed(2)} points off the dial, and over ₹1 crore more than ${s.errPerCrorePt.toFixed(2)} — identities from the limit, not estimates.`);
  if (c) parts.push(`Measured on ${spec.board} at ₹${spec.stakeRupees}: the take came out ${c.takePct.toFixed(2)}% against a ${spec.targetPct}% dial. ${ladderSentence(spec, key)}`);
  if (p.az > spec.azNoBindMax) {
    parts.push(`⚠ Above this game's clamp: the shared selection law will not bend the board past a_z ${spec.azNoBindMax.toFixed(2)} here, so part of the extra authority is simply not applied.`);
  }
  return parts.join(' ');
}

/** Custom-value text. B and the error are exact; the ladder is only measured at the three presets. */
export function customConsequence(spec: ReserveGameSpec, az: number): string {
  if (!Number.isFinite(az) || az < AUTHORITY_MIN || az > AUTHORITY_MAX) {
    return `Type an authority between ${AUTHORITY_MIN} and ${AUTHORITY_MAX}. 0.15 is the measured default; higher steers harder and leaves a bigger clue for a player counting losses, lower drifts further before correcting.`;
  }
  const s = sizingAt(spec, az);
  const lo = RESERVE_PRESETS.filter((p) => p.az <= az).sort((a, b) => b.az - a.az)[0];
  const hi = RESERVE_PRESETS.filter((p) => p.az >= az).sort((a, b) => a.az - b.az)[0];
  const parts = [
    `Authority ${az} re-sizes this game's working scale to ${inr(s.boundRupees)} and its limit to ${inr(s.limitRupees)}.`,
    s.errPerLakhPt >= 5
      ? `Over ₹1 lakh of play the arithmetic allows ${s.errPerLakhPt.toFixed(1)} points either side of the dial — it promises nothing over a short run; it closes to one point once about ${inr(200 * s.limitRupees)} has been played, and to ${s.errPerCrorePt.toFixed(2)} points over ₹1 crore.`
      : `Over ₹1 lakh of play the take can sit at most ${s.errPerLakhPt.toFixed(2)} points off the dial; over ₹1 crore, ${s.errPerCrorePt.toFixed(2)}.`,
    `Each round moves the aim by ${(s.detectabilitySigmaPerRound).toFixed(4)} of a standard deviation, so twelve losses in a row move it by ${(12 * s.detectabilitySigmaPerRound).toFixed(3)}.`,
  ];
  const named = [lo, hi].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i);
  if (named.length) {
    parts.push(`The ladder is only MEASURED at the three presets — nearest ${named.length > 1 ? 'measured settings' : 'measured setting'}: `
      + named.map((p) => `${p!.label} (${p!.az}) ${ladderSentence(spec, p!.key).replace(/^After six losses in a row, the next round pays /, '')}`).join(' · '));
  }
  if (az > spec.azNoBindMax) {
    parts.push(`⚠ Above ${spec.azNoBindMax.toFixed(2)} the shared selection law clips the aim on this game's worst board, so part of this authority is not applied.`);
  }
  return parts.join(' ');
}

/* ── WHAT THE BANKROLL MEANS FOR EACH GAME (2026-09-16) ────────────────────────────────────────
 * The owner asked for this in as many words: "put the note which is recommended, which value
 * would do what and what is needed, what is not — and add that text somewhere in each game
 * differently as each game is different."
 *
 * They ARE different, and the difference is not decoration. The house-funding guard charges the
 * FULL payout against capacity (`adminSharePct: 100` in plinko.controller.js — deliberately
 * conservative, it demands more than the admin will actually be asked for). So the bankroll a
 * game needs before its advertised top prize can be paid is simply that game's biggest single
 * payout. Thimbles needs ₹2,940. Cash Casino needs ₹2,50,000. That is an 85x spread across one
 * platform, which is why a single sentence about "the bankroll" would be useless on seven of the
 * eight cards.
 *
 * WHAT HAPPENS WHEN THE BANKROLL IS TOO SMALL is the thing to understand: nothing breaks and no
 * error appears. The engine simply stops offering the outcomes it cannot fund, so the big
 * multipliers quietly become unreachable — exactly the state plinko was in for 838 drops. A
 * bankroll below a game's top payout does not make that game safer; it makes its own paytable a
 * lie. Either fund it or lower that game's max payout so the board tells the truth. */
export type BankrollVerdict = {
  needRupees: number;
  covered: boolean;
  headroom: number;
  /** one line naming what this game does to a bankroll, in its own terms */
  character: string;
  /** what the CURRENT figure buys on this game */
  verdict: string;
};

const CHARACTER: Record<string, string> = {
  plinko:
    'Solo and fast — one player, one drop, settled instantly. Exposure is one ball at a time, so the bankroll only ever has to cover a single top slot. The cheapest big-prize game on the platform to fund.',
  lucky7:
    'A shared table: one card settles everybody at once. Several players can be on the winning card together, so the real exposure is the WHOLE table, not one seat — treat its need as a floor, not a ceiling, once more than a handful play at once.',
  thimbles:
    'The smallest exposure you have. Low stakes, one player, a 2.94x top. Almost any bankroll funds it fully — it is never the game that needs your money.',
  roulette:
    'A straight-up number pays 29:1, and a slip can carry many of them. This is the largest single-spin exposure on the platform alongside the other table games, and unlike the crash games it can all land on one number.',
  aviator:
    'A crash game, and the danger is correlation: when the plane flies to a high multiplier, EVERY player still in the round cashes out together. One good round can pay many seats at once, so size it above the single-seat figure if a crowd plays.',
  andar_bahar:
    'Near even-money, but players pile onto the same side. The board looks tame and the correlated exposure is not — a whole table can win together on one card.',
  teen_patti:
    'Pays about 1.98x, so no single seat is dramatic, but like Andar Bahar the table tends to back the same hand. Its risk is width, not height.',
  casino:
    'The biggest top prize you offer, and a solo slot — one spin, one player, the full jackpot. It is the single most expensive outcome on the platform to be able to pay.',
};

/** What the CURRENT bankroll actually buys on one game. Plain language, no jargon. */
export function bankrollVerdictFor(gameId: string, bankrollRupees: number | null): BankrollVerdict | null {
  const spec = RESERVE_GAME_SPECS[gameId];
  if (!spec) return null;
  const need = spec.maxSinglePayoutRupees;
  const have = Number.isFinite(Number(bankrollRupees)) ? Number(bankrollRupees) : 0;
  const covered = have >= need;
  const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
  let verdict: string;
  if (!bankrollRupees) {
    verdict = `No bankroll is set, so the system is falling back to its ₹50,000 placeholder. On this game that ${50000 >= need ? 'happens to cover' : 'does NOT cover'} the ${inr(need)} top prize. Set a real figure — the placeholder is a guess, not a decision.`;
  } else if (covered) {
    const times = have / need;
    verdict =
      `${inr(have)} covers this game's ${inr(need)} top prize` +
      (times >= 2
        ? ` with ${times.toFixed(1)}x headroom, so a losing run does not quietly put the big multipliers out of reach.`
        : ` — but only just. After ${inr(have - need)} of drawdown the top prize stops being payable and the board goes back to advertising something it cannot deliver. ${inr(need * 2)} would give it real room.`);
  } else {
    verdict =
      `${inr(have)} does NOT cover this game's ${inr(need)} top prize. Nothing will break and no error will show — the engine simply stops offering the outcomes it cannot fund, so the biggest multipliers become unreachable while still printed on the board. Either raise the bankroll to ${inr(need)}+ or lower this game's max payout so the paytable tells the truth.`;
  }
  return { needRupees: need, covered, headroom: have - need, character: CHARACTER[gameId] ?? '', verdict };
}
